// v2/pages/admin/AdminQuotesV2.js — Yonetim / Teklifler (/v2/admin/quotes)
// Filtreler adres cubugunda: ?q= &source=musteri|admin &status=ready|expired|cart|ordered &page= &per=
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FileText, Plus, Search, X, RotateCw, ChevronLeft, ChevronRight, Inbox, Loader2, Upload,
  FileSpreadsheet, Store, Users, Clock, CheckCircle, TrendingUp, Download, Info, Share2,
  Trash2, AlertTriangle,
} from 'lucide-react';
import ToastStack from '../../components/Toast';
import useToast from '../../hooks/useToast';
import { useAdminQuoteList, adminQuoteApi } from '../../hooks/useAdminQuotes';
import { quoteApi, QUOTE_STATUS, fmtTarih, kalanGun, karsilamaOrani, fmtEur } from '../../hooks/useQuotes';
import { formatTRY, formatTRYShort, formatNumber } from '../../utils/format';
import css from '../../styles/QuotesV2.module.css';
import a from '../../styles/AdminQuotesV2.module.css';
import '../../styles/tokens.css';

const KAYNAK = [
  { id: '', label: 'Tüm kaynaklar' },
  { id: 'musteri', label: 'Müşterinin oluşturduğu' },
  { id: 'admin', label: 'Yönetimin oluşturduğu' },
];
const DURUM = [
  { id: '', label: 'Tüm durumlar' },
  { id: 'ready', label: 'Açık (geçerli)' },
  { id: 'expired', label: 'Süresi dolan' },
  { id: 'cart', label: 'Sepete aktarılan' },
  { id: 'ordered', label: 'Siparişe dönen' },
];
const PER = ['20', '50', '100'];
const MAX_MB = 5;
const UZANTILAR = ['.xlsx', '.xlsm', '.csv'];

// ---------------------------------------------------------------- rozetler
export function KaynakRozet({ q }) {
  return q.source === 'admin'
    ? <span className={`${css.pill} ${a.srcAdmin}`}>Yönetim</span>
    : <span className={`${css.pill} ${a.srcCustomer}`}>Müşteri</span>;
}

export function PaylasimRozet({ q }) {
  if (q.source !== 'admin') return null;
  return q.shared
    ? <span className={a.shareOn} title={`Müşteri panelinde · ${fmtTarih(q.sharedAt)}`}><Share2 size={11} /> Müşteride</span>
    : <span className={a.shareOff} title="Müşteri panelinde görünmüyor">Müşteriye kapalı</span>;
}

// ---------------------------------------------------------------- silme
/** Silme onayi. teklifler: [{no, status, cari:{code,title}, lineCount}] */
export function SilModal({ teklifler, onKapat, onSilindi }) {
  const [mesgul, setMesgul] = useState(false);
  const [hata, setHata] = useState(null);
  const aktarilmis = teklifler.filter((q) => ['Sepete Aktarıldı', 'Siparişe Dönüştü'].includes(q.status));

  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape' && !mesgul) onKapat(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [mesgul, onKapat]);

  const sil = async () => {
    setMesgul(true);
    setHata(null);
    try {
      const v = await adminQuoteApi.remove(teklifler.map((q) => q.no));
      onSilindi(v);
    } catch (e) {
      setHata(e.message || 'Silinemedi');
      setMesgul(false);
    }
  };

  return (
    <div className={css.modalBack} role="presentation"
         onMouseDown={(e) => { if (e.target === e.currentTarget && !mesgul) onKapat(); }}>
      <div className={css.modal} role="alertdialog" aria-modal="true" aria-labelledby="teklif-sil">
        <div className={css.modalHead}>
          <h2 id="teklif-sil" className={`${css.modalTitle} ${a.dangerTitle}`}>
            <Trash2 size={18} /> {teklifler.length === 1 ? 'Teklifi Sil' : `${teklifler.length} Teklifi Sil`}
          </h2>
          <button type="button" className={css.iconBtn} onClick={onKapat} disabled={mesgul} aria-label="Kapat">
            <X size={16} />
          </button>
        </div>
        <div className={css.modalBody}>
          <p className={a.delText}>
            Seçilen {teklifler.length === 1 ? 'teklif' : 'teklifler'} ve tüm kalemleri <b>kalıcı olarak</b> silinecek.
            Bu işlem geri alınamaz.
          </p>
          <div className={a.delList}>
            {teklifler.map((q) => (
              <span key={q.no} className={a.delRow}>
                <b>{q.no}</b>
                <span className={a.delCari}>{(q.cari && (q.cari.title || q.cari.code)) || ''}</span>
                <span className={css.muted}>{formatNumber(q.lineCount || 0)} kalem</span>
              </span>
            ))}
          </div>
          {aktarilmis.length > 0 && (
            <div className={a.delWarn}>
              <AlertTriangle size={15} />
              <span>
                {aktarilmis.map((q) => q.no).join(', ')} müşteri tarafından sepete aktarılmış / siparişe dönüştürülmüş.
                Yalnızca teklif kaydı silinir; <b>sepet, sipariş ve stok kayıtları değişmez.</b>
              </span>
            </div>
          )}
          {hata && <div className={css.alertRed} role="alert">{hata}</div>}
        </div>
        <div className={css.modalFoot}>
          <button type="button" className={css.btnGhost} onClick={onKapat} disabled={mesgul}>Vazgeç</button>
          <button type="button" className={a.btnDanger} onClick={sil} disabled={mesgul}>
            {mesgul ? <Loader2 size={15} className={css.spin} /> : <Trash2 size={15} />}
            Kalıcı Olarak Sil
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- yeni teklif
function CariSecici({ secili, onSec }) {
  const [ara, setAra] = useState('');
  const [liste, setListe] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState(null);

  useEffect(() => {
    const q = ara.trim();
    if (q.length < 2) { setListe([]); setHata(null); return undefined; }
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      setYukleniyor(true);
      adminQuoteApi.customers(q, ctrl.signal)
        .then((v) => { setListe(v.results || []); setHata(null); })
        .catch((e) => { if (e.name !== 'AbortError') setHata(e.message || 'Cari aranamadı'); })
        .finally(() => { if (!ctrl.signal.aborted) setYukleniyor(false); });
    }, 300);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [ara]);

  if (secili) {
    return (
      <div className={a.cariChip}>
        <span className={a.cariIcon}><Store size={18} /></span>
        <span className={a.cariBody}>
          <span className={a.cariTitle}>{secili.title || '—'}</span>
          <span className={a.cariCode}>
            Cari: {secili.code}
            {secili.b2b && <span className={a.b2bTag}>B2B kullanıcısı</span>}
          </span>
        </span>
        <button type="button" className={css.iconBtn} onClick={() => onSec(null)} aria-label="Cariyi değiştir">
          <X size={15} />
        </button>
      </div>
    );
  }

  return (
    <div className={a.cariSearch}>
      <span className={css.searchBox} style={{ flex: '1 1 auto', minWidth: 0 }}>
        <Search size={14} className={css.searchIcon} />
        <input className={css.searchInput} value={ara} autoFocus
               onChange={(e) => setAra(e.target.value)}
               placeholder="Cari kodu veya ünvanı yazın (en az 2 karakter)…" aria-label="Cari ara" />
        {yukleniyor && <Loader2 size={14} className={`${css.spin} ${a.searchSpin}`} />}
      </span>
      {hata && <div className={css.alertRed}>{hata}</div>}
      {liste.length > 0 && (
        <div className={a.cariList} role="listbox">
          {liste.map((c) => (
            <button key={c.code} type="button" role="option" aria-selected="false"
                    className={a.cariOpt} onClick={() => onSec(c)}>
              <span className={a.cariOptMain}>
                <span className={a.cariOptTitle}>{c.title || '—'}</span>
                <span className={a.cariOptCode}>{c.code}</span>
              </span>
              <span className={a.cariOptMeta}>
                {c.b2b && <span className={a.b2bTag}>B2B</span>}
                {c.quotes > 0 && <span className={a.quoteTag}>{formatNumber(c.quotes)} teklif</span>}
              </span>
            </button>
          ))}
        </div>
      )}
      {ara.trim().length >= 2 && !yukleniyor && !hata && liste.length === 0 && (
        <span className={css.muted}>Eşleşen cari bulunamadı.</span>
      )}
    </div>
  );
}

function YeniTeklifModal({ onKapat, onBasari }) {
  const [cari, setCari] = useState(null);
  const [dosya, setDosya] = useState(null);
  const [not, setNot] = useState('');
  const [mesgul, setMesgul] = useState(false);
  const [hata, setHata] = useState(null);
  const input = useRef(null);

  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape' && !mesgul) onKapat(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [mesgul, onKapat]);

  const sec = (f) => {
    setHata(null);
    if (!f) return;
    const ad = (f.name || '').toLowerCase();
    if (!UZANTILAR.some((u) => ad.endsWith(u))) {
      setHata(ad.endsWith('.xls') ? 'Eski Excel biçimi (.xls) desteklenmiyor; .xlsx olarak kaydedin.'
                                  : 'Lütfen .xlsx veya .csv dosyası seçin.');
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) { setHata(`Dosya en fazla ${MAX_MB} MB olabilir.`); return; }
    setDosya(f);
  };

  const gonder = async () => {
    if (!cari || !dosya || mesgul) return;
    setMesgul(true);
    setHata(null);
    try {
      const v = await adminQuoteApi.create(cari.code, dosya, not.trim());
      onBasari(v.quote);
    } catch (e) {
      setHata(e.message || 'Teklif oluşturulamadı');
      setMesgul(false);
    }
  };

  return (
    <div className={css.modalBack} role="presentation"
         onMouseDown={(e) => { if (e.target === e.currentTarget && !mesgul) onKapat(); }}>
      <div className={css.modal} role="dialog" aria-modal="true" aria-labelledby="yeni-teklif">
        <div className={css.modalHead}>
          <h2 id="yeni-teklif" className={css.modalTitle}><FileText size={18} /> Yeni Teklif</h2>
          <button type="button" className={css.iconBtn} onClick={onKapat} disabled={mesgul} aria-label="Kapat">
            <X size={16} />
          </button>
        </div>
        <div className={css.modalBody}>
          <div className={a.step}>
            <span className={a.stepNo}>1</span>
            <span className={a.stepTitle}>Cari seçin</span>
          </div>
          <CariSecici secili={cari} onSec={setCari} />

          <div className={a.step}>
            <span className={a.stepNo}>2</span>
            <span className={a.stepTitle}>Ürün listesini yükleyin</span>
            <button type="button" className={`${css.linkBtn} ${a.stepLink}`} onClick={() => quoteApi.template()}>
              <Download size={12} /> Şablonu indir
            </button>
          </div>
          <div
            className={`${css.drop} ${dosya ? css.dropFull : ''} ${a.dropSmall}`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); if (!mesgul) sec(e.dataTransfer.files && e.dataTransfer.files[0]); }}
            onClick={() => { if (!mesgul && !dosya && input.current) input.current.click(); }}
            role="button" tabIndex={0}
            onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !dosya && input.current) input.current.click(); }}
          >
            <input ref={input} type="file" accept={UZANTILAR.join(',')} hidden
                   onChange={(e) => { sec(e.target.files && e.target.files[0]); e.target.value = ''; }} />
            {dosya ? (
              <div className={css.fileRow}>
                <span className={css.fileIcon}><FileSpreadsheet size={24} /></span>
                <span className={css.fileBody}>
                  <span className={css.fileName}>{dosya.name}</span>
                  <span className={css.fileMeta}>{formatNumber(dosya.size / 1024, 0)} KB</span>
                </span>
                {!mesgul && (
                  <button type="button" className={css.iconBtn} aria-label="Dosyayı kaldır"
                          onClick={(e) => { e.stopPropagation(); setDosya(null); }}>
                    <X size={15} />
                  </button>
                )}
              </div>
            ) : (
              <>
                <span className={css.dropIcon}><Upload size={24} /></span>
                <span className={css.dropTitle}>Excel dosyasını sürükleyin veya seçin</span>
                <span className={css.dropHint}>.xlsx, .csv · en fazla {MAX_MB} MB · 35.000 satır</span>
              </>
            )}
          </div>

          <label className={css.noteLabel}>
            Teklif notu <span className={css.optional}>(isteğe bağlı, PDF'te görünür)</span>
            <textarea className={css.note} rows={2} maxLength={500} value={not} disabled={mesgul}
                      onChange={(e) => setNot(e.target.value)} placeholder="Örn. Telefonla iletilen talep" />
          </label>

          <p className={css.pvNote}>
            <Info size={13} /> Fiyatlar seçilen carinin indirim zinciri ve geçerli kampanyalarla hesaplanır.
            Teklif oluşturulduğunda müşteriye kapalıdır; PDF/Excel'i iletebilir veya müşteri paneline gönderebilirsiniz.
          </p>
          {hata && <div className={css.alertRed} role="alert">{hata}</div>}
        </div>
        <div className={css.modalFoot}>
          <button type="button" className={css.btnGhost} onClick={onKapat} disabled={mesgul}>Vazgeç</button>
          <button type="button" className={css.btnPrimary} onClick={gonder} disabled={!cari || !dosya || mesgul}>
            {mesgul ? <Loader2 size={15} className={css.spin} /> : <FileText size={15} />}
            {mesgul ? 'Teklif hazırlanıyor…' : 'Teklif Oluştur'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- sayfa
export default function AdminQuotesV2() {
  const navigate = useNavigate();
  const toast = useToast();
  const [sp, setSp] = useSearchParams();
  const [arama, setArama] = useState(sp.get('q') || '');
  const [yeni, setYeni] = useState(false);
  const [secim, setSecim] = useState(() => new Set());
  const [silinecek, setSilinecek] = useState(null);

  const params = useMemo(() => ({
    q: sp.get('q') || '',
    source: sp.get('source') || '',
    status: sp.get('status') || '',
    page: sp.get('page') || '1',
    per: PER.includes(sp.get('per')) ? sp.get('per') : '20',
  }), [sp]);
  const { data, loading, error, yenile } = useAdminQuoteList(params);

  const guncelle = useCallback((yama, sayfaSifirla = true) => {
    setSp((onceki) => {
      const p = new URLSearchParams(onceki);
      Object.entries(yama).forEach(([k, v]) => { if (v === '' || v == null) p.delete(k); else p.set(k, v); });
      if (sayfaSifirla) p.delete('page');
      if (p.get('per') === '20') p.delete('per');
      return p;
    }, { replace: true });
  }, [setSp]);

  useEffect(() => {
    const t = setTimeout(() => { if (arama.trim() !== (sp.get('q') || '')) guncelle({ q: arama.trim() }); }, 350);
    return () => clearTimeout(t);
  }, [arama, sp, guncelle]);

  const st = (data && data.stats) || {};
  const kartlar = [
    { k: 'total', label: 'Toplam Teklif', v: formatNumber(st.total || 0), Icon: FileText, tone: 'kNavy' },
    { k: 'today', label: 'Bugün', v: formatNumber(st.today || 0), Icon: Clock, tone: 'tBlue' },
    { k: 'open', label: 'Açık Teklif', v: formatNumber(st.open || 0), Icon: CheckCircle, tone: 'tGreen' },
    { k: 'ordered', label: 'Siparişe Dönen', v: formatNumber(st.ordered || 0), Icon: TrendingUp, tone: 'tAmber' },
    { k: 'net30', label: 'Son 30 Gün Net', v: formatTRYShort(st.net30 || 0), tam: formatTRY(st.net30 || 0), Icon: TrendingUp, tone: 'tPurple' },
    { k: 'c30', label: 'Son 30 Gün Cari', v: formatNumber(st.customers30 || 0), Icon: Users, tone: 'tGray' },
  ];
  const sayfa = Number(params.page) || 1;
  const sonSayfa = (data && data.total_pages) || 1;
  const filtreVar = Boolean(params.q || params.source || params.status);
  const satirlar = (data && data.results) || [];
  const sayfaTumSecili = satirlar.length > 0 && satirlar.every((q) => secim.has(q.no));
  const secimDegis = (no) => setSecim((s0) => {
    const n = new Set(s0);
    if (n.has(no)) n.delete(no); else n.add(no);
    return n;
  });
  const sayfayiSec = () => setSecim((s0) => {
    const n = new Set(s0);
    satirlar.forEach((q) => (sayfaTumSecili ? n.delete(q.no) : n.add(q.no)));
    return n;
  });
  const seciliTeklifler = satirlar.filter((q) => secim.has(q.no));

  return (
    <div className={a.page}>
      <ToastStack items={toast.items} onDismiss={toast.dismiss} />
      <div className={a.head}>
        <div>
          <h2 className={a.title}>Teklifler</h2>
          <p className={a.sub}>Müşterilerin oluşturduğu ve yönetimin hazırladığı tüm teklifler</p>
        </div>
        <span className={a.headActions}>
          <button type="button" className={css.iconBtn} onClick={yenile} aria-label="Yenile" title="Yenile">
            <RotateCw size={15} />
          </button>
          <button type="button" className={css.btnPrimary} onClick={() => setYeni(true)}>
            <Plus size={16} /> Yeni Teklif
          </button>
        </span>
      </div>

      <div className={a.statGrid}>
        {kartlar.map(({ k, label, v, tam, Icon, tone }) => (
          <div key={k} className={a.stat} title={tam || undefined}>
            <span className={`${a.statIcon} ${css[tone]}`}><Icon size={17} /></span>
            <span className={a.statBody}>
              <span className={a.statLabel}>{label}</span>
              <span className={a.statValue}>{v}</span>
            </span>
          </div>
        ))}
      </div>

      <section className={css.card} style={{ marginBottom: 0 }}>
        <div className={css.toolbar}>
          <span className={css.searchBox}>
            <Search size={14} className={css.searchIcon} />
            <input className={css.searchInput} value={arama} onChange={(e) => setArama(e.target.value)}
                   placeholder="Teklif no, cari kodu / ünvanı, dosya adı…" aria-label="Tekliflerde ara" />
            {arama && (
              <button type="button" className={css.searchClear} onClick={() => setArama('')} aria-label="Temizle">
                <X size={12} />
              </button>
            )}
          </span>
          <select className={css.select} value={params.source} onChange={(e) => guncelle({ source: e.target.value })}
                  aria-label="Kaynak">
            {KAYNAK.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          <select className={css.select} value={params.status} onChange={(e) => guncelle({ status: e.target.value })}
                  aria-label="Durum">
            {DURUM.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          <span className={css.spacer} />
          {secim.size > 0 && (
            <button type="button" className={a.btnDangerGhost}
                    onClick={() => setSilinecek(seciliTeklifler.length ? seciliTeklifler : Array.from(secim).map((no) => ({ no })))}>
              <Trash2 size={14} /> Seçilenleri Sil ({formatNumber(secim.size)})
            </button>
          )}
          {filtreVar && (
            <button type="button" className={css.btnGhost} onClick={() => { setArama(''); setSp(new URLSearchParams(), { replace: true }); }}>
              <X size={13} /> Temizle
            </button>
          )}
        </div>

        {error && <div className={css.alertRed}>{error}</div>}

        {loading && !data ? (
          <div className={css.skelList}>{[0, 1, 2, 3].map((i) => <div key={i} className={css.skel} />)}</div>
        ) : !data || data.results.length === 0 ? (
          <div className={css.empty}>
            <Inbox size={32} strokeWidth={1.3} />
            <span>{filtreVar ? 'Bu filtrelere uygun teklif yok.' : 'Henüz teklif oluşturulmamış.'}</span>
          </div>
        ) : (
          <div className={`${css.tableScroll} ${loading ? css.fading : ''}`}>
            <table className={css.table}>
              <thead>
                <tr>
                  <th className={a.cSel}>
                    <input type="checkbox" checked={sayfaTumSecili} onChange={sayfayiSec}
                           aria-label="Bu sayfadaki teklifleri seç" />
                  </th>
                  <th>Teklif No</th>
                  <th>Cari</th>
                  <th>Kaynak</th>
                  <th>Tarih</th>
                  <th>Geçerlilik</th>
                  <th className={css.right}>Kalem</th>
                  <th>Karşılama</th>
                  <th className={css.right}>Net Tutar</th>
                  <th>Durum</th>
                  <th aria-label="İşlem" />
                </tr>
              </thead>
              <tbody>
                {data.results.map((q) => {
                  const durum = QUOTE_STATUS[q.status] || QUOTE_STATUS['Hazır'];
                  const oran = karsilamaOrani(q);
                  const gun = kalanGun(q.validUntil);
                  return (
                    <tr key={q.no} className={secim.has(q.no) ? a.rowSel : undefined}>
                      <td className={a.cSel}>
                        <input type="checkbox" checked={secim.has(q.no)} onChange={() => secimDegis(q.no)}
                               aria-label={`${q.no} seç`} />
                      </td>
                      <td className={css.cMain}>
                        <Link to={`/v2/admin/quotes/${q.no}`} className={css.quoteNo}>{q.no}</Link>
                        {q.fileName && <span className={css.subText}>{q.fileName}</span>}
                      </td>
                      <td data-label="Cari">
                        <span className={a.cariCell}>
                          <b>{q.cari.title || '—'}</b>
                          <span className={css.subText}>{q.cari.code}</span>
                        </span>
                      </td>
                      <td data-label="Kaynak">
                        <span className={a.srcCell}>
                          <KaynakRozet q={q} />
                          <PaylasimRozet q={q} />
                          {q.createdBy && <span className={css.subText}>{q.createdBy}</span>}
                        </span>
                      </td>
                      <td data-label="Tarih">{fmtTarih(q.createdAt)}</td>
                      <td data-label="Geçerlilik">
                        <span className={css.validCell}>
                          {fmtTarih(q.validUntil, false)}
                          {!q.expired && gun !== null && gun >= 0 && (
                            <span className={`${css.daysLeft} ${gun <= 1 ? css.daysWarn : ''}`}>
                              {gun === 0 ? 'bugün' : `${gun} gün`}
                            </span>
                          )}
                        </span>
                      </td>
                      <td className={css.right} data-label="Kalem">{formatNumber(q.lineCount)}</td>
                      <td data-label="Karşılama">
                        <span className={css.rateCell}>
                          <span className={css.rateTrack}><span className={css.rateFill} style={{ width: `${oran}%` }} /></span>
                          <span className={css.rateText}>%{formatNumber(oran, 0)}</span>
                        </span>
                      </td>
                      <td className={css.right} data-label="Net Tutar">
                        <span className={css.priceCell}>
                          <span className={css.money}>{formatTRY(q.totals.net)}</span>
                          {q.totalsEur && <span className={css.eur}>{fmtEur(q.totalsEur.net)}</span>}
                        </span>
                      </td>
                      <td data-label="Durum"><span className={`${css.pill} ${css[durum.tone]}`}>{durum.label}</span></td>
                      <td className={`${css.right} ${css.cAct}`}>
                        <span className={a.rowActs}>
                          <Link to={`/v2/admin/quotes/${q.no}`} className={css.btnSmall}>Aç</Link>
                          <button type="button" className={a.iconDanger} onClick={() => setSilinecek([q])}
                                  aria-label={`${q.no} sil`} title="Sil">
                            <Trash2 size={14} />
                          </button>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {data && data.total_items > 0 && (
          <div className={css.pager}>
            <span className={css.pagerInfo}><b>{formatNumber(data.total_items)}</b> teklif · Sayfa {sayfa}/{sonSayfa}</span>
            <label className={css.perLabel}>
              Sayfa başına
              <select className={css.select} value={params.per} onChange={(e) => guncelle({ per: e.target.value })}>
                {PER.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <span className={css.spacer} />
            <button type="button" className={css.pageBtn} disabled={sayfa <= 1 || loading}
                    onClick={() => guncelle({ page: String(sayfa - 1) }, false)} aria-label="Önceki sayfa">
              <ChevronLeft size={15} />
            </button>
            <button type="button" className={css.pageBtn} disabled={sayfa >= sonSayfa || loading}
                    onClick={() => guncelle({ page: String(sayfa + 1) }, false)} aria-label="Sonraki sayfa">
              <ChevronRight size={15} />
            </button>
          </div>
        )}
      </section>

      {silinecek && (
        <SilModal
          teklifler={silinecek}
          onKapat={() => setSilinecek(null)}
          onSilindi={(v) => {
            setSilinecek(null);
            setSecim(new Set());
            toast.push(v.message || 'Teklif silindi', 'success');
            yenile();
          }}
        />
      )}

      {yeni && (
        <YeniTeklifModal
          onKapat={() => setYeni(false)}
          onBasari={(q) => {
            setYeni(false);
            toast.push(`${q.no} oluşturuldu (${q.cari.title || q.cari.code})`, 'success');
            navigate(`/v2/admin/quotes/${q.no}`);
          }}
        />
      )}
    </div>
  );
}
